import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// Define the currency types
export type CurrencyCode = 'DZD' | 'USD' | 'EUR';

export interface Currency {
  code: CurrencyCode;
  name: {
    en: string;
    ar: string;
  };
  symbol: string;
  symbolNative: string;
  decimalDigits: number;
  rounding: number;
}

export interface CurrencyState {
  selectedCurrency: CurrencyCode;
  exchangeRates: {
    [key: string]: number;
  };
}

interface CurrencyAction {
  type: string;
  payload?: any;
}

// Define available currencies
export const CURRENCIES: Record<CurrencyCode, Currency> = {
  DZD: {
    code: 'DZD',
    name: { en: 'Algerian Dinar', ar: 'الدينار الجزائري' },
    symbol: 'د.ج',
    symbolNative: 'د.ج',
    decimalDigits: 2,
    rounding: 0
  },
  USD: {
    code: 'USD',
    name: { en: 'US Dollar', ar: 'الدولار الأمريكي' },
    symbol: '$',
    symbolNative: '$',
    decimalDigits: 2,
    rounding: 0
  },
  EUR: {
    code: 'EUR',
    name: { en: 'Euro', ar: 'اليورو' },
    symbol: '€',
    symbolNative: '€',
    decimalDigits: 2,
    rounding: 0
  }
};

// Default exchange rates (relative to DZD)
const DEFAULT_EXCHANGE_RATES = {
  DZD: 1, // 1 DZD = 1 DZD
  USD: 149, // 1 USD ≈ 149 DZD
  EUR: 164  // 1 EUR ≈ 164 DZD
};

// Initial state
const initialState: CurrencyState = {
  selectedCurrency: 'DZD',
  exchangeRates: DEFAULT_EXCHANGE_RATES
};

// Action types
const SET_CURRENCY = 'SET_CURRENCY';
const SET_EXCHANGE_RATES = 'SET_EXCHANGE_RATES';

// Reducer
const currencyReducer = (state: CurrencyState, action: CurrencyAction): CurrencyState => {
  switch (action.type) {
    case SET_CURRENCY:
      return {
        ...state,
        selectedCurrency: action.payload
      };
    case SET_EXCHANGE_RATES:
      return {
        ...state,
        exchangeRates: {
          ...state.exchangeRates,
          ...action.payload
        }
      };
    default:
      return state;
  }
};

// Context
interface CurrencyContextType {
  state: CurrencyState;
  dispatch: React.Dispatch<CurrencyAction>;
  setSelectedCurrency: (currency: CurrencyCode) => void;
  convertAmount: (amount: number, fromCurrency?: CurrencyCode, toCurrency?: CurrencyCode) => number;
  formatCurrency: (amount: number, currencyCode?: CurrencyCode) => string;
  getCurrencyInfo: (currencyCode: CurrencyCode) => Currency;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// Provider component
export const CurrencyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(currencyReducer, initialState);

  const setSelectedCurrency = (currency: CurrencyCode) => {
    dispatch({ type: SET_CURRENCY, payload: currency });
  };

  const convertAmount = (amount: number, fromCurrency?: CurrencyCode, toCurrency?: CurrencyCode): number => {
    const from = fromCurrency || 'DZD'; // Default source currency
    const to = toCurrency || state.selectedCurrency; // Default to selected currency

    if (from === to) return amount;

    // Convert from source currency to DZD (base currency)
    // If from is DZD, multiply by 1
    // If from is USD and 1 USD = 149 DZD, multiply by 149
    // If from is EUR and 1 EUR = 164 DZD, multiply by 164
    let amountInDZD: number;
    if (from === 'DZD') {
      amountInDZD = amount;
    } else {
      amountInDZD = amount * state.exchangeRates[from];
    }

    // Convert from DZD to target currency
    // If to is DZD, return the amount as is
    // If to is USD and 1 USD = 149 DZD, divide by 149
    // If to is EUR and 1 EUR = 164 DZD, divide by 164
    if (to === 'DZD') {
      return amountInDZD;
    } else {
      return amountInDZD / state.exchangeRates[to];
    }
  };

  const formatCurrency = (amount: number, currencyCode?: CurrencyCode): string => {
    const currency = getCurrencyInfo(currencyCode || state.selectedCurrency);

    // Use Arabic formatting for DZD and standard for others
    if (currency.code === 'DZD') {
      // Format with Algerian Dinar formatting
      return new Intl.NumberFormat('ar-DZ', {
        style: 'currency',
        currency: 'DZD',
        minimumFractionDigits: currency.decimalDigits,
        maximumFractionDigits: currency.decimalDigits
      }).format(amount);
    } else {
      // Format with standard formatting for other currencies
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.code,
        minimumFractionDigits: currency.decimalDigits,
        maximumFractionDigits: currency.decimalDigits
      }).format(amount);
    }
  };

  const getCurrencyInfo = (currencyCode: CurrencyCode): Currency => {
    return CURRENCIES[currencyCode];
  };

  const value: CurrencyContextType = {
    state,
    dispatch,
    setSelectedCurrency,
    convertAmount,
    formatCurrency,
    getCurrencyInfo
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Custom hook
export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};